// Help > Glossary. Every term in both languages, written here rather than
// translated on the fly: the Greek follows the vocabulary of the two user
// manuals (Help menu), so the glossary, the manuals and the screens use the
// same words. Numbers follow the platform format (1,000 / 2.5).
//
// group: "btm" self-consumption and the bill · "pv" PV and battery technology
//        "market" market and grid · "finance" economics and financing

export const GLOSSARY_GROUPS = ["btm", "pv", "market", "finance"];

export const GLOSSARY = [
  // ---- Self-consumption and the bill ---------------------------------------
  {
    group: "btm",
    term: { en: "Behind the meter (BTM)", el: "Πίσω από τον μετρητή (BTM)" },
    desc: {
      en: "A PV system, with or without a battery, installed on the consumer's side of the meter to cover the site's own consumption. Its benefit is the electricity bill the customer avoids, not sales to the market.",
      el: "Φ/Β σύστημα, με ή χωρίς μπαταρία, που εγκαθίσταται στην πλευρά του καταναλωτή, πίσω από τον μετρητή, για να καλύπτει τη δική του κατανάλωση. Το όφελος είναι ο λογαριασμός ρεύματος που αποφεύγει ο πελάτης, όχι πωλήσεις στην αγορά.",
    },
  },
  {
    group: "btm",
    term: { en: "Self-consumption", el: "Αυτοκατανάλωση" },
    desc: {
      en: "The share of PV production used on site, directly or through the battery. The rest is exported to the grid or, under zero feed-in, curtailed.",
      el: "Το ποσοστό της παραγωγής του Φ/Β που καταναλώνεται επιτόπου, απευθείας ή μέσω της μπαταρίας. Το υπόλοιπο εγχέεται στο δίκτυο ή, σε μηδενική έγχυση, περικόπτεται.",
    },
  },
  {
    group: "btm",
    term: { en: "Self-sufficiency", el: "Αυτάρκεια" },
    desc: {
      en: "The share of the site's consumption covered by the PV and the battery.",
      el: "Το ποσοστό της κατανάλωσης της εγκατάστασης που καλύπτεται από το Φ/Β και την μπαταρία.",
    },
  },
  {
    group: "btm",
    term: { en: "Annual savings (year 1)", el: "Ετήσια εξοικονόμηση (έτος 1)" },
    desc: {
      en: "The bill without the system minus the bill with it, plus the value of the energy exported. The platform computes both bills for every hour of the year.",
      el: "Ο λογαριασμός χωρίς το σύστημα μείον τον λογαριασμό με το σύστημα, συν την αξία της ενέργειας που εγχέεται. Η πλατφόρμα υπολογίζει και τους δύο λογαριασμούς για κάθε ώρα του έτους.",
    },
  },
  {
    group: "btm",
    term: { en: "Feed-in regime", el: "Καθεστώς έγχυσης" },
    desc: {
      en: "Whether and how the system may send energy to the grid: export allowed, export from PV only, or zero feed-in. It decides which battery settings are allowed; the platform locks the others and explains why.",
      el: "Αν και πώς επιτρέπεται στο σύστημα να στέλνει ενέργεια στο δίκτυο: επιτρέπεται εξαγωγή, έγχυση μόνο από Φ/Β ή μηδενική έγχυση. Από αυτό εξαρτάται ποιες ρυθμίσεις της μπαταρίας επιτρέπονται· η πλατφόρμα κλειδώνει τις υπόλοιπες και εξηγεί γιατί.",
    },
  },
  {
    group: "btm",
    term: { en: "Net billing", el: "Net billing (ενεργειακός συμψηφισμός με χρηματική αποτίμηση)" },
    desc: {
      en: "Energy exported to the grid is valued in money, at the hourly day-ahead price or at a fixed price agreed with the aggregator (ΦΟΣΕ), and the credit is netted against the cost of the energy still bought from the grid.",
      el: "Η ενέργεια που εγχέεται στο δίκτυο αποτιμάται σε χρήμα, στην ωριαία τιμή της αγοράς επόμενης ημέρας ή σε σταθερή τιμή που συμφωνείται με τον ΦΟΣΕ, και η πίστωση συμψηφίζεται με το κόστος της ενέργειας που εξακολουθεί να αγοράζεται από το δίκτυο.",
    },
  },
  {
    group: "btm",
    term: { en: "Net metering", el: "Net metering (ενεργειακός συμψηφισμός σε kWh)" },
    desc: {
      en: "The older scheme in which exported kWh are credited as kWh against later consumption. It is not modelled in the platform; studies use net billing.",
      el: "Το παλαιότερο σχήμα, όπου οι kWh που εγχέονται πιστώνονται ως kWh και συμψηφίζονται με μεταγενέστερη κατανάλωση. Η πλατφόρμα δεν το μοντελοποιεί· οι μελέτες γίνονται με net billing.",
    },
  },
  {
    group: "btm",
    term: { en: "Zero feed-in", el: "Μηδενική έγχυση" },
    desc: {
      en: "No energy is exported to the grid. PV output that the site can neither use nor store in the battery is curtailed. Used where the grid is saturated or there is no netting contract.",
      el: "Δεν εγχέεται καθόλου ενέργεια στο δίκτυο. Η παραγωγή του Φ/Β που δεν καταναλώνεται ούτε αποθηκεύεται στην μπαταρία περικόπτεται. Χρησιμοποιείται σε κορεσμένο δίκτυο ή όταν δεν υπάρχει σύμβαση συμψηφισμού.",
    },
  },
  {
    group: "btm",
    term: { en: "Export from PV only", el: "Έγχυση μόνο από Φ/Β" },
    desc: {
      en: "The PV exports its surplus; the battery only serves the site's load and never exports. Typical when the battery has its own inverter and a meter that does not export.",
      el: "Το Φ/Β εγχέει την περίσσειά του· η μπαταρία εξυπηρετεί μόνο το φορτίο της εγκατάστασης και δεν εγχέει ποτέ. Συνηθίζεται όταν η μπαταρία έχει δικό της αντιστροφέα και μετρητή που δεν εγχέει.",
    },
  },
  {
    group: "btm",
    term: { en: "Load profile", el: "Προφίλ κατανάλωσης" },
    desc: {
      en: "The site's consumption for every hour (or every 15 minutes) of a year. A measured file from the meter gives the most reliable study; otherwise the profile is estimated from the annual or monthly consumption and a typical daily shape.",
      el: "Η κατανάλωση της εγκατάστασης για κάθε ώρα (ή κάθε 15 λεπτά) ενός έτους. Ένα αρχείο μετρήσεων από τον μετρητή δίνει την πιο αξιόπιστη μελέτη· διαφορετικά το προφίλ εκτιμάται από την ετήσια ή τη μηνιαία κατανάλωση και ένα τυπικό ημερήσιο σχήμα.",
    },
  },
  {
    group: "btm",
    term: { en: "Retail tariff", el: "Τιμολόγιο ρεύματος" },
    desc: {
      en: "What the customer pays for each kWh bought from the grid: the supplier's energy price plus regulated charges (network, ΥΚΩ and others) and taxes. Self-consumption avoids only the charges that depend on the kWh bought, not the fixed ones.",
      el: "Ό,τι πληρώνει ο πελάτης για κάθε kWh που αγοράζει από το δίκτυο: η τιμή ενέργειας του προμηθευτή συν τις ρυθμιζόμενες χρεώσεις (δίκτυο, ΥΚΩ κ.ά.) και τους φόρους. Η αυτοκατανάλωση γλιτώνει μόνο τις χρεώσεις που εξαρτώνται από τις kWh που αγοράζονται, όχι τις πάγιες.",
    },
  },
  {
    group: "btm",
    term: { en: "Time-of-use tariff", el: "Τιμολόγιο με ζώνες χρέωσης" },
    desc: {
      en: "A retail tariff with different prices for different hours of the day (e.g. a cheaper night zone). The battery can shift energy from cheap to expensive zones.",
      el: "Τιμολόγιο με διαφορετική τιμή σε διαφορετικές ώρες της ημέρας (π.χ. φθηνότερη νυχτερινή ζώνη). Η μπαταρία μπορεί να μεταφέρει ενέργεια από τις φθηνές στις ακριβές ζώνες.",
    },
  },
  {
    group: "btm",
    term: { en: "Dynamic tariff", el: "Δυναμικό τιμολόγιο" },
    desc: {
      en: "A retail tariff whose energy price follows the day-ahead market hour by hour. The battery can then also charge in cheap hours and discharge in expensive ones, where the feed-in regime allows it.",
      el: "Τιμολόγιο του οποίου η τιμή ενέργειας ακολουθεί κάθε ώρα την αγορά επόμενης ημέρας. Τότε η μπαταρία μπορεί επιπλέον να φορτίζει στις φθηνές ώρες και να εκφορτίζει στις ακριβές, όπου το επιτρέπει το καθεστώς έγχυσης.",
    },
  },
  {
    group: "btm",
    term: { en: "ΥΚΩ (public service obligations)", el: "ΥΚΩ (Υπηρεσίες Κοινής Ωφέλειας)" },
    desc: {
      en: "A regulated charge per kWh on the Greek electricity bill.",
      el: "Ρυθμιζόμενη χρέωση ανά kWh στον λογαριασμό ρεύματος.",
    },
  },
  {
    group: "btm",
    term: { en: "ΦΟΣΕ (aggregator) / BRP", el: "ΦΟΣΕ / BRP" },
    desc: {
      en: "The aggregator that represents small producers in the market and, under net billing, settles the energy exported. In the market it also acts as the balance responsible party (BRP) and charges for imbalances.",
      el: "Φορέας σωρευτικής εκπροσώπησης: εκπροσωπεί μικρούς παραγωγούς στην αγορά και, στο net billing, εκκαθαρίζει την ενέργεια που εγχέεται. Στην αγορά λειτουργεί και ως υπεύθυνος εξισορρόπησης (BRP) και χρεώνει τις αποκλίσεις.",
    },
  },
  {
    group: "btm",
    term: { en: "ΔΕΔΔΗΕ (distribution network operator)", el: "ΔΕΔΔΗΕ" },
    desc: {
      en: "The Hellenic Electricity Distribution Network Operator. It runs the low- and medium-voltage network, issues connection terms and keeps the meter readings; customers can usually obtain their hourly or 15-minute consumption from it.",
      el: "Ο Διαχειριστής του Ελληνικού Δικτύου Διανομής Ηλεκτρικής Ενέργειας. Διαχειρίζεται το δίκτυο χαμηλής και μέσης τάσης, δίνει τους όρους σύνδεσης και τηρεί τις μετρήσεις των μετρητών· από αυτόν ο πελάτης μπορεί συνήθως να λάβει την κατανάλωσή του ανά ώρα ή ανά 15 λεπτά.",
    },
  },

  // ---- PV and battery --------------------------------------------------------
  {
    group: "pv",
    term: { en: "kW and kWh", el: "kW και kWh" },
    desc: {
      en: "kW is power, the rate at a given moment; kWh is energy, power over time. A battery has both: e.g. 5 kW / 10 kWh can deliver 5 kW for about two hours.",
      el: "Το kW είναι ισχύς, ο ρυθμός σε μια δεδομένη στιγμή· η kWh είναι ενέργεια, ισχύς επί χρόνο. Μια μπαταρία έχει και τα δύο: π.χ. 5 kW / 10 kWh δίνει 5 kW για περίπου δύο ώρες.",
    },
  },
  {
    group: "pv",
    term: { en: "kWp (kilowatt-peak)", el: "kWp (κιλοβάτ αιχμής)" },
    desc: {
      en: "The rated DC power of the PV modules under standard test conditions (1,000 W/m², 25 °C). Actual output is usually lower.",
      el: "Η ονομαστική ισχύς DC των πλαισίων υπό πρότυπες συνθήκες δοκιμής (1,000 W/m², 25 °C). Η πραγματική ισχύς είναι συνήθως χαμηλότερη.",
    },
  },
  {
    group: "pv",
    term: { en: "Specific yield", el: "Ειδική παραγωγή" },
    desc: {
      en: "Annual PV energy per installed kWp (kWh/kWp). In Greece it is typically about 1,300–1,600 kWh/kWp, depending on location and orientation.",
      el: "Η ετήσια ενέργεια του Φ/Β ανά εγκατεστημένο kWp (kWh/kWp). Στην Ελλάδα είναι συνήθως περίπου 1,300–1,600 kWh/kWp, ανάλογα με την τοποθεσία και τον προσανατολισμό.",
    },
  },
  {
    group: "pv",
    term: { en: "PR (performance ratio)", el: "PR (λόγος απόδοσης)" },
    desc: {
      en: "Actual PV production divided by the theoretical production from the same irradiance. It sums up all losses: temperature, shading, soiling, cables and inverter.",
      el: "Η πραγματική παραγωγή του Φ/Β διαιρεμένη με τη θεωρητική για την ίδια ακτινοβολία. Συγκεντρώνει όλες τις απώλειες: θερμοκρασία, σκίαση, ρύπανση, καλώδια και αντιστροφέα.",
    },
  },
  {
    group: "pv",
    term: { en: "DC/AC ratio", el: "Λόγος DC/AC" },
    desc: {
      en: "Module power divided by inverter power. Above 1, the inverter clips some midday output in exchange for more energy in the rest of the day.",
      el: "Η ισχύς των πλαισίων προς την ισχύ του αντιστροφέα. Πάνω από 1, ο αντιστροφέας περικόπτει λίγη μεσημεριανή παραγωγή, με αντάλλαγμα περισσότερη ενέργεια τις υπόλοιπες ώρες.",
    },
  },
  {
    group: "pv",
    term: { en: "String", el: "String" },
    desc: {
      en: "PV modules connected in series. Its voltage must stay within the inverter's limits in both cold and hot weather.",
      el: "Πλαίσια συνδεδεμένα σε σειρά. Η τάση του πρέπει να μένει μέσα στα όρια του αντιστροφέα, τόσο στο κρύο όσο και στη ζέστη.",
    },
  },
  {
    group: "pv",
    term: { en: "MPPT", el: "MPPT" },
    desc: {
      en: "The inverter's maximum power point tracker. Each MPPT takes one or more strings, which should share the same orientation.",
      el: "Ο ανιχνευτής σημείου μέγιστης ισχύος του αντιστροφέα. Κάθε MPPT δέχεται ένα ή περισσότερα strings, που πρέπει να έχουν τον ίδιο προσανατολισμό.",
    },
  },
  {
    group: "pv",
    term: { en: "Hybrid inverter", el: "Υβριδικός αντιστροφέας" },
    desc: {
      en: "An inverter that takes both the PV strings and a battery on its DC side. It is what makes DC coupling possible.",
      el: "Αντιστροφέας που δέχεται στην πλευρά DC τόσο τα strings του Φ/Β όσο και μπαταρία. Αυτός επιτρέπει τη σύζευξη DC.",
    },
  },
  {
    group: "pv",
    term: { en: "AC / DC coupling", el: "Σύζευξη AC / DC" },
    desc: {
      en: "AC coupling: the battery has its own inverter and connects on the AC side. DC coupling: the battery connects to the PV's hybrid inverter and can charge with energy the inverter would otherwise clip.",
      el: "Σύζευξη AC: η μπαταρία έχει δικό της αντιστροφέα και συνδέεται στην πλευρά AC. Σύζευξη DC: η μπαταρία συνδέεται στον υβριδικό αντιστροφέα του Φ/Β και μπορεί να φορτίζει με ενέργεια που αλλιώς θα περικοπτόταν από τον αντιστροφέα.",
    },
  },
  {
    group: "pv",
    term: { en: "Curtailment", el: "Περικοπή παραγωγής" },
    desc: {
      en: "PV production deliberately reduced because the energy can be neither used, nor stored, nor exported (e.g. under zero feed-in or a grid limit).",
      el: "Σκόπιμη μείωση της παραγωγής του Φ/Β, όταν η ενέργεια δεν μπορεί ούτε να καταναλωθεί, ούτε να αποθηκευτεί, ούτε να εγχυθεί (π.χ. σε μηδενική έγχυση ή λόγω ορίου του δικτύου).",
    },
  },
  {
    group: "pv",
    term: { en: "TMY (typical meteorological year)", el: "TMY (τυπικό μετεωρολογικό έτος)" },
    desc: {
      en: "A representative year of hourly weather built from many years of records, used to estimate annual production.",
      el: "Ένα αντιπροσωπευτικό έτος ωριαίων μετεωρολογικών δεδομένων, που συντίθεται από πολλά έτη μετρήσεων και χρησιμοποιείται για την εκτίμηση της ετήσιας παραγωγής.",
    },
  },
  {
    group: "pv",
    term: { en: "GHI / DNI", el: "GHI / DNI" },
    desc: {
      en: "Global horizontal irradiance / direct normal irradiance: the total sunlight on a horizontal surface and the direct beam on a surface facing the sun.",
      el: "Ολική οριζόντια / άμεση κάθετη ηλιακή ακτινοβολία: η συνολική ακτινοβολία σε οριζόντια επιφάνεια και η άμεση δέσμη σε επιφάνεια κάθετη προς τον ήλιο.",
    },
  },
  {
    group: "pv",
    term: { en: "GCR (ground coverage ratio)", el: "GCR (λόγος κάλυψης εδάφους)" },
    desc: {
      en: "Module area divided by ground area: how densely the rows are placed. A higher GCR fits more modules but increases row-to-row shading.",
      el: "Η επιφάνεια των πλαισίων προς την επιφάνεια του εδάφους: πόσο πυκνά είναι τοποθετημένες οι σειρές. Μεγαλύτερος GCR χωρά περισσότερα πλαίσια αλλά αυξάνει την αλληλοσκίαση των σειρών.",
    },
  },
  {
    group: "pv",
    term: { en: "BESS", el: "BESS" },
    desc: {
      en: "Battery energy storage system.",
      el: "Σύστημα αποθήκευσης ενέργειας με μπαταρίες.",
    },
  },
  {
    group: "pv",
    term: { en: "Usable capacity", el: "Ωφέλιμη χωρητικότητα" },
    desc: {
      en: "The part of the battery's nominal energy that can actually be used, within the allowed state-of-charge range.",
      el: "Το μέρος της ονομαστικής ενέργειας της μπαταρίας που μπορεί πράγματι να χρησιμοποιηθεί, μέσα στα επιτρεπόμενα όρια κατάστασης φόρτισης.",
    },
  },
  {
    group: "pv",
    term: { en: "SoC / SoH (state of charge / health)", el: "SoC / SoH (κατάσταση φόρτισης / υγείας)" },
    desc: {
      en: "SoC: the battery's energy level as a percentage of usable capacity. SoH: the capacity left after ageing, as a percentage of the original.",
      el: "SoC: το επίπεδο ενέργειας της μπαταρίας ως ποσοστό της ωφέλιμης χωρητικότητας. SoH: η χωρητικότητα που απομένει μετά τη γήρανση, ως ποσοστό της αρχικής.",
    },
  },
  {
    group: "pv",
    term: { en: "C-rate", el: "C-rate" },
    desc: {
      en: "Battery power divided by usable energy. 1C means a full charge or discharge in about one hour; 0.5C in about two.",
      el: "Η ισχύς της μπαταρίας προς την ωφέλιμη ενέργεια. 1C σημαίνει πλήρη φόρτιση ή εκφόρτιση σε περίπου μία ώρα· 0.5C σε περίπου δύο.",
    },
  },
  {
    group: "pv",
    term: { en: "Round-trip efficiency (RTE)", el: "Απόδοση πλήρους κύκλου (RTE)" },
    desc: {
      en: "The energy the battery returns as a percentage of the energy it absorbed, over a full charge-discharge cycle.",
      el: "Η ενέργεια που επιστρέφει η μπαταρία ως ποσοστό της ενέργειας που απορρόφησε, σε έναν πλήρη κύκλο φόρτισης και εκφόρτισης.",
    },
  },
  {
    group: "pv",
    term: { en: "Cycle", el: "Κύκλος" },
    desc: {
      en: "One full charge and discharge of the usable capacity. Warranties limit cycles per year or total energy throughput, so cycles drive battery wear.",
      el: "Μία πλήρης φόρτιση και εκφόρτιση της ωφέλιμης χωρητικότητας. Οι εγγυήσεις περιορίζουν τους κύκλους ανά έτος ή τη συνολική ενέργεια που διακινείται, γι' αυτό οι κύκλοι καθορίζουν τη φθορά της μπαταρίας.",
    },
  },
  {
    group: "pv",
    term: { en: "Battery wear price", el: "Τιμή φθοράς μπαταρίας" },
    desc: {
      en: "A cost per kWh discharged that the battery dispatch takes into account, so that the battery only cycles when the saving is worth the wear.",
      el: "Κόστος ανά kWh εκφόρτισης που λαμβάνει υπόψη η κατανομή της μπαταρίας, ώστε η μπαταρία να κάνει κύκλο μόνο όταν η εξοικονόμηση αξίζει τη φθορά.",
    },
  },
  {
    group: "pv",
    term: { en: "Degradation", el: "Υποβάθμιση" },
    desc: {
      en: "The gradual loss of output over the years: PV modules lose a small percentage per year, and batteries lose usable capacity with age and cycles. The study applies it year by year.",
      el: "Η σταδιακή μείωση της απόδοσης με τα χρόνια: τα πλαίσια χάνουν ένα μικρό ποσοστό κάθε χρόνο και οι μπαταρίες χάνουν ωφέλιμη χωρητικότητα με την ηλικία και τους κύκλους. Η μελέτη την εφαρμόζει έτος προς έτος.",
    },
  },
  {
    group: "pv",
    term: { en: "OEM warranty curve", el: "Καμπύλη εγγύησης κατασκευαστή" },
    desc: {
      en: "The manufacturer's guaranteed capacity-retention table (e.g. 97% in year 1, 85% in year 10). Lenders accept it as the basis for battery degradation because it is contractual rather than estimated.",
      el: "Ο πίνακας του κατασκευαστή με την εγγυημένη διατήρηση χωρητικότητας (π.χ. 97% το 1ο έτος, 85% το 10ο). Οι τράπεζες τον δέχονται ως βάση για την υποβάθμιση της μπαταρίας, επειδή είναι συμβατικός και όχι εκτίμηση.",
    },
  },
  {
    group: "pv",
    term: { en: "OEM (original equipment manufacturer)", el: "OEM (κατασκευαστής εξοπλισμού)" },
    desc: {
      en: "The company that built the equipment; for a battery, the cell or system manufacturer whose datasheet and warranty terms govern it.",
      el: "Η εταιρεία που κατασκεύασε τον εξοπλισμό· για μια μπαταρία, ο κατασκευαστής των κυψελών ή του συστήματος, του οποίου το φύλλο προδιαγραφών και οι όροι εγγύησης τη διέπουν.",
    },
  },
  {
    group: "pv",
    term: { en: "Augmentation", el: "Αναπλήρωση χωρητικότητας" },
    desc: {
      en: "Adding cells or racks to an existing battery so that usable capacity returns to its original level. Standard mid-life practice for large storage; it carries its own capital cost.",
      el: "Προσθήκη κυψελών ή racks σε υπάρχουσα μπαταρία, ώστε η ωφέλιμη χωρητικότητα να επανέλθει στο αρχικό της επίπεδο. Συνήθης πρακτική στη μέση της ζωής μεγάλων συστημάτων αποθήκευσης· έχει το δικό της κόστος επένδυσης.",
    },
  },
  {
    group: "pv",
    term: { en: "PCS (power conversion system)", el: "PCS (σύστημα μετατροπής ισχύος)" },
    desc: {
      en: "The battery's bidirectional inverter, between its DC side and the AC network. Its conversion losses depend on the operating load.",
      el: "Ο αμφίδρομος αντιστροφέας της μπαταρίας, ανάμεσα στην πλευρά DC της και στο δίκτυο AC. Οι απώλειές του εξαρτώνται από το φορτίο λειτουργίας.",
    },
  },
  {
    group: "pv",
    term: { en: "No-load loss (P0)", el: "Απώλειες κενού φορτίου (P0)" },
    desc: {
      en: "The constant (iron) losses of a transformer while it is energised, regardless of the power flowing through it.",
      el: "Οι σταθερές απώλειες σιδήρου ενός μετασχηματιστή όσο βρίσκεται υπό τάση, ανεξάρτητα από την ισχύ που τον διαρρέει.",
    },
  },
  {
    group: "pv",
    term: { en: "Load loss (Pk)", el: "Απώλειες φορτίου (Pk)" },
    desc: {
      en: "The load-dependent (copper) losses of a transformer at rated power; they scale with the square of the loading.",
      el: "Οι απώλειες χαλκού ενός μετασχηματιστή στην ονομαστική ισχύ· μεταβάλλονται με το τετράγωνο της φόρτισης.",
    },
  },

  // ---- Market and grid -------------------------------------------------------
  {
    group: "market",
    term: { en: "Day-ahead market (DAM)", el: "Αγορά επόμενης ημέρας (DAM)" },
    desc: {
      en: "The Energy Exchange market where the price of every hour (or every 15 minutes) of the next day is set. Battery dispatch is optimised against these prices, and net-billing exports can be valued at them.",
      el: "Η αγορά του Χρηματιστηρίου Ενέργειας όπου ορίζεται η τιμή κάθε ώρας (ή κάθε 15 λεπτών) της επόμενης ημέρας. Με αυτές τις τιμές βελτιστοποιείται η κατανομή της μπαταρίας και μπορεί να αποτιμηθεί η έγχυση στο net billing.",
    },
  },
  {
    group: "market",
    term: { en: "Intraday market (ID)", el: "Ενδοημερήσια αγορά (ID)" },
    desc: {
      en: "Market sessions closer to real time, used to adjust positions after the day-ahead market.",
      el: "Συνεδριάσεις της αγοράς πιο κοντά στον πραγματικό χρόνο, για διόρθωση των θέσεων μετά την αγορά επόμενης ημέρας.",
    },
  },
  {
    group: "market",
    term: { en: "Arbitrage", el: "Arbitrage (εκμετάλλευση διαφοράς τιμών)" },
    desc: {
      en: "Charging in cheap hours and discharging in expensive ones.",
      el: "Φόρτιση στις φθηνές ώρες και εκφόρτιση στις ακριβές.",
    },
  },
  {
    group: "market",
    term: { en: "aFRR", el: "aFRR" },
    desc: {
      en: "Automatic frequency restoration reserve: activated automatically to correct frequency deviations.",
      el: "Αυτόματη εφεδρεία αποκατάστασης συχνότητας: ενεργοποιείται αυτόματα για να διορθώσει αποκλίσεις της συχνότητας.",
    },
  },
  {
    group: "market",
    term: { en: "mFRR", el: "mFRR" },
    desc: {
      en: "Manual frequency restoration reserve: activated on instruction from the system operator.",
      el: "Χειροκίνητη εφεδρεία αποκατάστασης συχνότητας: ενεργοποιείται κατ' εντολή του Διαχειριστή του Συστήματος.",
    },
  },
  {
    group: "market",
    term: { en: "Imbalance cost", el: "Κόστος αποκλίσεων" },
    desc: {
      en: "The charge from the aggregator or BRP for the difference between the scheduled and the actual injection.",
      el: "Η χρέωση του ΦΟΣΕ ή του υπεύθυνου εξισορρόπησης για τη διαφορά μεταξύ προγράμματος και πραγματικής έγχυσης.",
    },
  },
  {
    group: "market",
    term: { en: "Imperfect foresight", el: "Ατελής πρόβλεψη" },
    desc: {
      en: "The revenue gap between knowing prices perfectly in advance and operating on forecasts.",
      el: "Η διαφορά εσόδων ανάμεσα στην τέλεια γνώση των τιμών εκ των προτέρων και στη λειτουργία με προβλέψεις.",
    },
  },
  {
    group: "market",
    term: { en: "Category 11A / 11B", el: "Κατηγορία 11Α / 11Β" },
    desc: {
      en: "Greek categories of renewable plants with storage, with different rules on remuneration, charging and export.",
      el: "Κατηγορίες σταθμών ΑΠΕ με αποθήκευση, με διαφορετικούς κανόνες αμοιβής, φόρτισης και έγχυσης.",
    },
  },
  {
    group: "market",
    term: { en: "Reference price", el: "Τιμή αναφοράς" },
    desc: {
      en: "Under the Greek Category 11A framework, the fixed price at which PV energy injected at the connection point is paid for the whole year. It carries regulatory risk rather than market price risk, so it is not merchant revenue.",
      el: "Στο πλαίσιο της Κατηγορίας 11Α, η σταθερή τιμή με την οποία αμείβεται για όλο το έτος η ενέργεια του Φ/Β που εγχέεται στο σημείο σύνδεσης. Φέρει ρυθμιστικό κίνδυνο και όχι κίνδυνο τιμής αγοράς, γι' αυτό δεν είναι έσοδο αγοράς (merchant).",
    },
  },
  {
    group: "market",
    term: { en: "Merchant exposure", el: "Έκθεση στην αγορά (merchant)" },
    desc: {
      en: "The share of revenue sold at market prices with no contract or regulated price behind it. It drives the price side of the downside cases, so a partly regulated project should never be described as fully exposed.",
      el: "Το μερίδιο των εσόδων που πωλείται σε τιμές αγοράς, χωρίς σύμβαση ή ρυθμιζόμενη τιμή. Καθορίζει το σκέλος τιμής των δυσμενών σεναρίων, γι' αυτό ένα έργο με εν μέρει ρυθμιζόμενα έσοδα δεν πρέπει να περιγράφεται ως πλήρως εκτεθειμένο.",
    },
  },
  {
    group: "market",
    term: { en: "POI (point of interconnection)", el: "Σημείο σύνδεσης (POI)" },
    desc: {
      en: "The physical point where the plant connects to the grid: where the export and import limits apply, where the metering sits and usually the step-up transformer. All grid flows in the model are measured there.",
      el: "Το φυσικό σημείο όπου ο σταθμός συνδέεται με το δίκτυο: εκεί ισχύουν τα όρια έγχυσης και απορρόφησης, εκεί βρίσκεται η μέτρηση και συνήθως ο μετασχηματιστής ανύψωσης. Όλες οι ροές προς και από το δίκτυο στο μοντέλο μετρώνται σε αυτό το σημείο.",
    },
  },
  {
    group: "market",
    term: { en: "Export / import limit", el: "Όριο έγχυσης / απορρόφησης" },
    desc: {
      en: "The maximum power the plant may send to / take from the grid at the connection point, as set in the connection terms.",
      el: "Η μέγιστη ισχύς που επιτρέπεται να δώσει / να πάρει ο σταθμός από το δίκτυο στο σημείο σύνδεσης, όπως ορίζεται στους όρους σύνδεσης.",
    },
  },
  {
    group: "market",
    term: { en: "ΑΔΜΗΕ (transmission system operator)", el: "ΑΔΜΗΕ" },
    desc: {
      en: "The Independent Power Transmission Operator: it runs the Greek high-voltage transmission system.",
      el: "Ο Ανεξάρτητος Διαχειριστής Μεταφοράς Ηλεκτρικής Ενέργειας: διαχειρίζεται το ελληνικό σύστημα μεταφοράς υψηλής τάσης.",
    },
  },

  // ---- Economics and financing -----------------------------------------------
  {
    group: "finance",
    term: { en: "CAPEX (capital expenditure)", el: "CAPEX (κόστος επένδυσης)" },
    desc: {
      en: "The one-off cost of building the plant: equipment, installation, grid connection, engineering and contingency. Quoted gross (before any grant) or net (after it); cost benchmarks are compared on the gross figure, because a grant changes what the owner pays, not what the plant costs to build.",
      el: "Το εφάπαξ κόστος κατασκευής: εξοπλισμός, εγκατάσταση, σύνδεση στο δίκτυο, μελέτη και απρόβλεπτα. Δίνεται μικτό (πριν από επιδότηση) ή καθαρό (μετά). Οι συγκρίσεις κόστους γίνονται στο μικτό ποσό, γιατί η επιδότηση αλλάζει το τι πληρώνει ο ιδιοκτήτης, όχι το τι κοστίζει η κατασκευή.",
    },
  },
  {
    group: "finance",
    term: { en: "OPEX (operating expenditure)", el: "OPEX (λειτουργικές δαπάνες)" },
    desc: {
      en: "The recurring annual cost of running the plant: maintenance, insurance, land lease and any augmentation allowance. It is charged every year whether or not the plant earns.",
      el: "Το ετήσιο κόστος λειτουργίας: συντήρηση, ασφάλιση, μίσθωση γης και τυχόν πρόβλεψη αναπλήρωσης χωρητικότητας. Χρεώνεται κάθε χρόνο, ανεξάρτητα από το αν το έργο έχει έσοδα.",
    },
  },
  {
    group: "finance",
    term: { en: "FOM (fixed operations and maintenance)", el: "FOM (σταθερό κόστος λειτουργίας και συντήρησης)" },
    desc: {
      en: "Yearly running costs that do not depend on how much the plant is used: service contracts, insurance, land lease and, for storage, the augmentation allowance. The counterpart is variable O&M, which grows with throughput.",
      el: "Ετήσιο κόστος που δεν εξαρτάται από το πόσο χρησιμοποιείται ο σταθμός: συμβόλαια συντήρησης, ασφάλιση, μίσθωση γης και, στην αποθήκευση, η πρόβλεψη αναπλήρωσης χωρητικότητας. Το αντίστοιχο μεταβλητό κόστος αυξάνεται με την ενέργεια που διακινείται.",
    },
  },
  {
    group: "finance",
    term: { en: "Grant (subsidy)", el: "Επιδότηση" },
    desc: {
      en: "The share of the investment covered by a support programme. It reduces the capital the owner puts in, not the cost of building the plant.",
      el: "Το ποσοστό του κόστους επένδυσης που καλύπτεται από πρόγραμμα ενίσχυσης. Μειώνει το κεφάλαιο που βάζει ο ιδιοκτήτης, όχι το κόστος κατασκευής.",
    },
  },
  {
    group: "finance",
    term: { en: "Discount rate", el: "Προεξοφλητικό επιτόκιο" },
    desc: {
      en: "The annual rate at which future amounts are brought back to today's value. It expresses the return the investor requires on the capital.",
      el: "Το ετήσιο επιτόκιο με το οποίο τα μελλοντικά ποσά ανάγονται σε σημερινή αξία. Εκφράζει την απόδοση που ζητά ο επενδυτής από το κεφάλαιό του.",
    },
  },
  {
    group: "finance",
    term: { en: "NPV (net present value)", el: "NPV (καθαρή παρούσα αξία)" },
    desc: {
      en: "Future cash flows discounted to today, less the capital invested. Positive means the project earns more than the return required of it; measured against CAPEX it shows how much margin there is before the case turns negative.",
      el: "Οι μελλοντικές ταμειακές ροές αναγμένες σε σημερινή αξία, μείον το κεφάλαιο της επένδυσης. Θετική τιμή σημαίνει ότι το έργο αποδίδει περισσότερο από την απόδοση που απαιτείται· σε σχέση με το CAPEX δείχνει πόσο περιθώριο υπάρχει πριν το αποτέλεσμα γίνει αρνητικό.",
    },
  },
  {
    group: "finance",
    term: { en: "IRR (internal rate of return)", el: "IRR (εσωτερικός βαθμός απόδοσης)" },
    desc: {
      en: "The discount rate at which NPV would be exactly zero. It is only meaningful read against the discount rate: the gap between them is the return earned above the cost of capital.",
      el: "Το προεξοφλητικό επιτόκιο στο οποίο η NPV θα ήταν ακριβώς μηδέν. Έχει νόημα μόνο σε σύγκριση με το προεξοφλητικό επιτόκιο: η διαφορά τους είναι η απόδοση πάνω από το κόστος του κεφαλαίου.",
    },
  },
  {
    group: "finance",
    term: { en: "WACC (weighted average cost of capital)", el: "WACC (μεσοσταθμικό κόστος κεφαλαίου)" },
    desc: {
      en: "The blended return required by debt and equity together, used as the discount rate. A project whose IRR is below it destroys value even when it shows an accounting profit.",
      el: "Η συνδυασμένη απόδοση που απαιτούν μαζί το δάνειο και τα ίδια κεφάλαια· χρησιμοποιείται ως προεξοφλητικό επιτόκιο. Ένα έργο με IRR κάτω από αυτό καταστρέφει αξία, ακόμη κι αν εμφανίζει λογιστικό κέρδος.",
    },
  },
  {
    group: "finance",
    term: { en: "Simple payback", el: "Απλή απόσβεση" },
    desc: {
      en: "The number of years until cumulative savings (or revenue) cover the investment, without discounting.",
      el: "Σε πόσα έτη η σωρευτική εξοικονόμηση (ή τα έσοδα) καλύπτει την επένδυση, χωρίς προεξόφληση.",
    },
  },
  {
    group: "finance",
    term: { en: "Discounted payback", el: "Προεξοφλημένη απόσβεση" },
    desc: {
      en: "The year in which the cumulative discounted cash flow first turns positive: how long the capital stays at risk in present-value terms. It comes later than simple payback.",
      el: "Το έτος κατά το οποίο η σωρευτική προεξοφλημένη ταμειακή ροή γίνεται για πρώτη φορά θετική: πόσο καιρό το κεφάλαιο μένει εκτεθειμένο, σε όρους παρούσας αξίας. Έρχεται αργότερα από την απλή απόσβεση.",
    },
  },
  {
    group: "finance",
    term: { en: "Profitability index", el: "Δείκτης αποδοτικότητας" },
    desc: {
      en: "The present value of the inflows divided by the capital invested. Above 1.0 means value is created; it expresses NPV per euro invested, so projects of different sizes can be compared.",
      el: "Η παρούσα αξία των εισροών διαιρεμένη με το κεφάλαιο της επένδυσης. Πάνω από 1.0 σημαίνει ότι δημιουργείται αξία· εκφράζει την NPV ανά ευρώ επένδυσης, ώστε να συγκρίνονται έργα διαφορετικού μεγέθους.",
    },
  },
  {
    group: "finance",
    term: { en: "LCOE (levelised cost of energy)", el: "LCOE (σταθμισμένο κόστος ενέργειας)" },
    desc: {
      en: "Discounted lifetime costs divided by discounted lifetime energy delivered: the price per MWh at which the project would exactly break even.",
      el: "Το προεξοφλημένο κόστος όλης της ζωής του έργου διαιρεμένο με την προεξοφλημένη ενέργεια που αποδίδει: η τιμή ανά MWh στην οποία το έργο θα έβγαινε ακριβώς χωρίς κέρδος ή ζημία.",
    },
  },
  {
    group: "finance",
    term: { en: "LCOS (levelised cost of storage)", el: "LCOS (σταθμισμένο κόστος αποθήκευσης)" },
    desc: {
      en: "The same calculation for the battery: discounted storage costs divided by discounted energy discharged. It is higher than LCOE because stored energy carries round-trip losses and cycle wear.",
      el: "Ο ίδιος υπολογισμός για την μπαταρία: το προεξοφλημένο κόστος αποθήκευσης διαιρεμένο με την προεξοφλημένη ενέργεια που εκφορτίζεται. Είναι υψηλότερο από το LCOE, γιατί η αποθηκευμένη ενέργεια έχει απώλειες πλήρους κύκλου και φθορά από τους κύκλους.",
    },
  },
  {
    group: "finance",
    term: { en: "EPC (engineering, procurement and construction)", el: "EPC (μελέτη, προμήθεια και κατασκευή)" },
    desc: {
      en: "The turnkey contract for building the plant. Published cost benchmarks are usually quoted EPC-inclusive, so an equipment price on its own is not comparable with them.",
      el: "Η σύμβαση μελέτης, προμήθειας και κατασκευής του σταθμού, που τον παραδίδει έτοιμο για λειτουργία. Οι δημοσιευμένοι δείκτες κόστους δίνονται συνήθως συμπεριλαμβανομένου του EPC, γι' αυτό μια τιμή μόνο εξοπλισμού δεν είναι συγκρίσιμη μαζί τους.",
    },
  },
  {
    group: "finance",
    term: { en: "BoP (balance of plant)", el: "BoP (λοιπός εξοπλισμός σταθμού)" },
    desc: {
      en: "Everything needed to make the main equipment work: transformers, cabling, protection, containers, controls and civil works. For storage it is a large share of the installed cost.",
      el: "Ό,τι χρειάζεται για να λειτουργήσει ο κύριος εξοπλισμός: μετασχηματιστές, καλωδιώσεις, προστασίες, κοντέινερ, συστήματα ελέγχου και έργα πολιτικού μηχανικού. Στην αποθήκευση είναι μεγάλο μέρος του κόστους εγκατάστασης.",
    },
  },
  {
    group: "finance",
    term: { en: "NREL ATB (annual technology baseline)", el: "NREL ATB (ετήσια βάση αναφοράς τεχνολογιών)" },
    desc: {
      en: "The cost and performance reference published each year by the US National Renewable Energy Laboratory. Its storage baseline charges augmentation as 2.5% of battery capital cost per year within fixed O&M, which keeps the system at its rated capacity over a 15-year life.",
      el: "Η αναφορά κόστους και απόδοσης που δημοσιεύει κάθε χρόνο το Εθνικό Εργαστήριο Ανανεώσιμης Ενέργειας των ΗΠΑ. Για την αποθήκευση χρεώνει την αναπλήρωση χωρητικότητας ως 2.5% του κόστους της μπαταρίας ανά έτος μέσα στο σταθερό κόστος λειτουργίας, ώστε το σύστημα να διατηρεί την ονομαστική του χωρητικότητα για 15 έτη.",
    },
  },
  {
    group: "finance",
    term: { en: "Salvage (residual) value", el: "Υπολειμματική αξία" },
    desc: {
      en: "The value assumed to remain in the assets at the end of the analysis horizon. It is credited to cash flows only when explicitly set; otherwise it is shown for information and left out of the returns.",
      el: "Η αξία που θεωρείται ότι απομένει στον εξοπλισμό στο τέλος του ορίζοντα της ανάλυσης. Προστίθεται στις ταμειακές ροές μόνο αν οριστεί ρητά· αλλιώς εμφανίζεται ενημερωτικά και δεν μετρά στις αποδόσεις.",
    },
  },
  {
    group: "finance",
    term: { en: "CFADS (cash flow available for debt service)", el: "CFADS (ταμειακή ροή διαθέσιμη για εξυπηρέτηση δανείου)" },
    desc: {
      en: "Revenue less operating costs and tax, before any debt payment: the cash a lender can actually be paid from. It is the numerator of every coverage ratio.",
      el: "Έσοδα μείον λειτουργικές δαπάνες και φόρους, πριν από οποιαδήποτε δόση δανείου: τα χρήματα από τα οποία μπορεί πράγματι να πληρωθεί η τράπεζα. Είναι ο αριθμητής κάθε δείκτη κάλυψης.",
    },
  },
  {
    group: "finance",
    term: { en: "DSCR (debt service coverage ratio)", el: "DSCR (δείκτης κάλυψης δόσης δανείου)" },
    desc: {
      en: "CFADS divided by that year's debt service. The binding test is the lowest year, not the average: one year below the covenant is a breach however comfortable the others are. Banks usually ask for more than 1.20–1.35.",
      el: "Η CFADS διαιρεμένη με τη δόση του δανείου του ίδιου έτους. Κρίσιμος είναι ο ελάχιστος δείκτης όλων των ετών, όχι ο μέσος όρος: ένα έτος κάτω από το όριο της σύμβασης είναι παράβαση, όσο άνετα κι αν είναι τα υπόλοιπα. Οι τράπεζες ζητούν συνήθως πάνω από 1.20–1.35.",
    },
  },
  {
    group: "finance",
    term: { en: "Minimum DSCR covenant", el: "Ελάχιστος DSCR της δανειακής σύμβασης" },
    desc: {
      en: "The coverage floor the loan agreement requires in every year. Falling below it is an event of default, so debt is normally sized to keep the worst modelled year above it, including in the downside case, not just the base case.",
      el: "Το κατώτατο όριο κάλυψης που απαιτεί η δανειακή σύμβαση σε κάθε έτος. Η πτώση κάτω από αυτό αποτελεί γεγονός αθέτησης, γι' αυτό το δάνειο διαστασιολογείται ώστε και το χειρότερο έτος να μένει πάνω από το όριο, και στο δυσμενές σενάριο, όχι μόνο στο βασικό.",
    },
  },
  {
    group: "finance",
    term: { en: "LLCR (loan life coverage ratio)", el: "LLCR (δείκτης κάλυψης διάρκειας δανείου)" },
    desc: {
      en: "Discounted cash available over the remaining loan life divided by the debt outstanding. It asks whether the loan can be repaid over its term, where DSCR asks whether this year's payment is covered.",
      el: "Η προεξοφλημένη διαθέσιμη ταμειακή ροή για την υπόλοιπη διάρκεια του δανείου διαιρεμένη με το ανεξόφλητο κεφάλαιο. Απαντά στο αν το δάνειο μπορεί να αποπληρωθεί μέσα στη διάρκειά του, ενώ ο DSCR στο αν καλύπτεται η φετινή δόση.",
    },
  },
  {
    group: "finance",
    term: { en: "PLCR (project life coverage ratio)", el: "PLCR (δείκτης κάλυψης διάρκειας έργου)" },
    desc: {
      en: "The LLCR test extended over the whole project life rather than the loan term, showing the cushion that remains after the loan matures.",
      el: "Ο ίδιος έλεγχος με τον LLCR, σε όλη τη διάρκεια ζωής του έργου αντί για τη διάρκεια του δανείου· δείχνει το περιθώριο που μένει μετά τη λήξη του δανείου.",
    },
  },
  {
    group: "finance",
    term: { en: "DSRA (debt service reserve account)", el: "DSRA (αποθεματικός λογαριασμός εξυπηρέτησης δανείου)" },
    desc: {
      en: "Cash set aside when the financing is signed, typically six months of debt service, that the lender can draw on if a year comes in short. It covers timing risk, not a structurally weak project.",
      el: "Χρήματα που δεσμεύονται κατά την υπογραφή της χρηματοδότησης, συνήθως δόσεις έξι μηνών, και που η τράπεζα μπορεί να χρησιμοποιήσει αν ένα έτος υστερήσει. Καλύπτει χρονικές αποκλίσεις, όχι ένα δομικά αδύναμο έργο.",
    },
  },
  {
    group: "finance",
    term: { en: "Tenor", el: "Διάρκεια δανείου (tenor)" },
    desc: {
      en: "The contractual life of the loan in years. A sculpted profile can clear the debt before the tenor ends, so the last year that actually carries debt service is worth stating alongside it.",
      el: "Η συμβατική διάρκεια του δανείου σε έτη. Με προσαρμοσμένη αποπληρωμή το δάνειο μπορεί να εξοφληθεί νωρίτερα, γι' αυτό αξίζει να αναφέρεται και το τελευταίο έτος με πραγματική δόση.",
    },
  },
  {
    group: "finance",
    term: { en: "Grace period (moratorium)", el: "Περίοδος χάριτος" },
    desc: {
      en: "The first years in which no principal is repaid. Interest is either paid as it falls due or capitalised into the balance; the two give very different coverage in the early years.",
      el: "Τα πρώτα έτη κατά τα οποία δεν αποπληρώνεται κεφάλαιο. Οι τόκοι είτε πληρώνονται κανονικά είτε κεφαλαιοποιούνται στο υπόλοιπο· οι δύο επιλογές δίνουν πολύ διαφορετική κάλυψη στα πρώτα έτη.",
    },
  },
  {
    group: "finance",
    term: { en: "Sculpted repayment", el: "Προσαρμοσμένη αποπληρωμή (sculpting)" },
    desc: {
      en: "A schedule in which each year's debt service is a fixed fraction of that year's cash flow, holding DSCR at the target rather than holding the payment constant. It suits revenue that changes over the asset's life.",
      el: "Πρόγραμμα όπου η δόση κάθε έτους είναι σταθερό κλάσμα της ταμειακής ροής του ίδιου έτους, ώστε ο DSCR να μένει στον στόχο αντί να μένει σταθερή η δόση. Ταιριάζει σε έσοδα που μεταβάλλονται στη διάρκεια ζωής του έργου.",
    },
  },
  {
    group: "finance",
    term: { en: "Gearing (leverage)", el: "Μόχλευση (gearing)" },
    desc: {
      en: "Debt as a share of total capital. Higher gearing raises the return on equity and lowers coverage; the level a lender will accept is set by the downside case.",
      el: "Το δάνειο ως ποσοστό του συνολικού κεφαλαίου. Μεγαλύτερη μόχλευση αυξάνει την απόδοση των ιδίων κεφαλαίων και μειώνει την κάλυψη· το επίπεδο που δέχεται η τράπεζα καθορίζεται από το δυσμενές σενάριο.",
    },
  },
  {
    group: "finance",
    term: { en: "P50 / P90 / P99", el: "P50 / P90 / P99" },
    desc: {
      en: "Values exceeded with 50% / 90% / 99% probability. P50 is the central estimate; P90 is expected to be beaten in nine years out of ten. P10 is the optimistic side. Because lenders size debt on the downside, the P90 minimum DSCR often matters more than the base case.",
      el: "Τιμές που ξεπερνιούνται με πιθανότητα 50% / 90% / 99%. Η P50 είναι η κεντρική εκτίμηση· η P90 αναμένεται να ξεπεραστεί εννέα χρονιές στις δέκα. Η P10 είναι η αισιόδοξη πλευρά. Επειδή οι τράπεζες διαστασιολογούν το δάνειο στο δυσμενές σενάριο, ο ελάχιστος DSCR στο P90 συχνά μετρά περισσότερο από το βασικό σενάριο.",
    },
  },
];

/** Lower case, no accents, final sigma folded: «Αυτοκατανάλωση» finds «αυτοκαταναλωση». */
export function foldForSearch(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/ς/g, "σ");
}

/**
 * The entries to show: one group (or all), matching the query in EITHER
 * language, so a Greek screen still finds «Net billing» when the user types
 * the English words. Terms whose name matches come first, then those that
 * only mention the words in their explanation; each part is alphabetical in
 * the screen's language.
 */
export function filterGlossary(items, { query = "", group = "all", lang = "el" } = {}) {
  const q = foldForSearch(query).trim();
  const words = q ? q.split(/\s+/) : [];
  const matches = (text) => words.every((w) => foldForSearch(text).includes(w));
  const byName = (a, b) => a.term[lang].localeCompare(b.term[lang], lang === "el" ? "el" : "en", { sensitivity: "base" });
  return items
    .filter((item) => group === "all" || item.group === group)
    .map((item) => ({
      item,
      inName: !words.length || matches(`${item.term.en} ${item.term.el}`),
      inText: !words.length || matches([item.term.en, item.term.el, item.desc.en, item.desc.el].join(" ")),
    }))
    .filter((row) => row.inText)
    .sort((a, b) => (a.inName === b.inName ? byName(a.item, b.item) : a.inName ? -1 : 1))
    .map((row) => row.item);
}
